/**
 * The chart widget's renderer (`specs/M10-D2-widgets.md` §5.1).
 *
 * ECharts, per ADR-001, with the **SVG renderer** — not the canvas one — for a reason this
 * project has already paid for twice: an interactive surface is verified by clicking, and
 * `elementFromPoint` needs a DOM node. With `renderer: 'svg'` every bar, slice, axis label and
 * legend entry is a real element a Playwright hit-test can reach.
 *
 * Only the pieces the account uses are registered, and the library itself is **not** in the
 * entry bundle: `echarts-runtime.ts` is reached through a dynamic `import()` below, so the
 * chart's ~600 kB is an async chunk only a dashboard downloads (q366).
 */
import { activeAxisKeys, COUNT_MEASURE_KEY, type ChartWidgetSettings } from '@elizmonday/shared';
import { useEffect, useMemo, useRef } from 'react';
import type { WidgetData } from '../types';
import { semanticColors } from '@elizmonday/ui';
import { SERIES_PALETTE, seriesColor } from './palette';
import type * as EChartsRuntime from './echarts-runtime';
import type { ChartInstance } from './echarts-runtime';

type ChartRuntime = typeof EChartsRuntime;
let runtime: Promise<ChartRuntime> | null = null;

/**
 * Loads (once per page) the ECharts chunk. Exported so a route can warm it before the canvas
 * mounts; the widget itself just awaits it inside its effect.
 */
export function loadChartRuntime(): Promise<ChartRuntime> {
  runtime ??= import('./echarts-runtime');
  return runtime;
}

/** monday's own type names → the ECharts series the capture shows. */
function seriesTypeFor(graphType: string): 'bar' | 'line' | 'pie' {
  switch (graphType) {
    case 'pie':
    case 'donut':
      return 'pie';
    case 'line':
    case 'area':
      return 'line';
    default:
      return 'bar';
  }
}

function cumulative(values: Array<number | null>): Array<number | null> {
  let running = 0;
  return values.map((value) => {
    if (value === null) return null;
    running += value;
    return running;
  });
}

export interface ChartWidgetProps {
  settings: ChartWidgetSettings;
  data: WidgetData | undefined;
  /** Series labels: a measure key is a column id, and the canvas knows the column's title. */
  measureTitle?: ((key: string) => string) | undefined;
}

export function buildChartOption(
  settings: ChartWidgetSettings,
  data: WidgetData,
  measureTitle: (key: string) => string,
): Record<string, unknown> {
  const type = seriesTypeFor(settings.graphType);
  const measures = activeAxisKeys(settings.yAxisColumns);
  const keys = measures.length > 0 ? measures : [COUNT_MEASURE_KEY];
  const categories = data.buckets.map((bucket) => bucket.label);

  if (type === 'pie') {
    const measure = keys[0]!;
    return {
      animation: false,
      tooltip: { trigger: 'item' },
      legend: {
        bottom: 0,
        icon: 'circle',
        itemWidth: 10,
        itemHeight: 10,
        textStyle: { fontSize: 12, color: semanticColors.primaryText },
        // monday's `flat_legend_type_percentage_value` puts the share next to the name.
        formatter:
          settings.flatLegendType === null
            ? undefined
            : (name: string) => {
                const bucket = data.buckets.find((entry) => entry.label === name);
                const total = data.buckets.reduce((sum, entry) => sum + (entry.values[measure] ?? 0), 0);
                const value = bucket?.values[measure] ?? 0;
                const share = total === 0 ? 0 : Math.round((value / total) * 100);
                return `${name}  ${share}%`;
              },
      },
      series: [
        {
          type: 'pie',
          // `donut` is monday's own second pie shape; `pie` is a full disc.
          radius: settings.graphType === 'donut' ? ['45%', '70%'] : '70%',
          center: ['50%', '45%'],
          label: { show: false },
          data: data.buckets.map((bucket, index) => ({
            name: bucket.label,
            value: bucket.values[measure] ?? 0,
            itemStyle: { color: bucket.color ?? seriesColor(index) },
          })),
        },
      ],
    };
  }

  const stacked = settings.graphType === 'stacked_column' || Object.keys(settings.zAxisColumns).length > 0;
  return {
    animation: false,
    tooltip: { trigger: 'axis' },
    grid: { left: 48, right: 16, top: 16, bottom: keys.length > 1 ? 48 : 32, containLabel: true },
    legend:
      keys.length > 1
        ? { bottom: 0, icon: 'circle', itemWidth: 10, itemHeight: 10, textStyle: { fontSize: 12, color: semanticColors.primaryText } }
        : { show: false },
    xAxis: {
      type: 'category',
      data: categories,
      axisLabel: { fontSize: 11, color: semanticColors.secondaryText },
      axisLine: { lineStyle: { color: semanticColors.layoutBorder } },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      axisLabel: { fontSize: 11, color: semanticColors.secondaryText },
      splitLine: { lineStyle: { color: semanticColors.allgreyBackground } },
    },
    series: keys.map((key, index) => {
      const raw = data.buckets.map((bucket) => bucket.values[key] ?? null);
      const values = settings.showCumulativeData ? cumulative(raw) : raw;
      return {
        name: measureTitle(key),
        type: settings.graphType === 'bar' ? 'bar' : type,
        stack: stacked ? 'total' : undefined,
        areaStyle: settings.graphType === 'area' ? {} : undefined,
        itemStyle: {
          // A dimension that carries its own colour (a status column) wins over the palette.
          color:
            keys.length === 1
              ? (params: { dataIndex: number }) =>
                  data.buckets[params.dataIndex]?.color ?? seriesColor(index)
              : seriesColor(index),
          borderRadius: type === 'bar' ? [2, 2, 0, 0] : 0,
        },
        data: values,
        markLine:
          index === 0 && settings.benchmarkLine !== null && settings.benchmarkLine.value !== ''
            ? {
                silent: true,
                symbol: 'none',
                lineStyle: { color: settings.benchmarkLine.color || semanticColors.negative },
                label: { formatter: settings.benchmarkLine.label || '' },
                data: [{ yAxis: Number(settings.benchmarkLine.value) }],
              }
            : undefined,
      };
    }),
  };
}

export function ChartWidget({ settings, data, measureTitle }: ChartWidgetProps) {
  const host = useRef<HTMLDivElement | null>(null);
  const chart = useRef<ChartInstance | null>(null);
  const title = useMemo(
    () => measureTitle ?? ((key: string) => (key === COUNT_MEASURE_KEY ? 'Count of items' : key)),
    [measureTitle],
  );

  const option = useMemo(
    () => (data === undefined ? null : buildChartOption(settings, data, title)),
    [settings, data, title],
  );

  useEffect(() => {
    const element = host.current;
    if (element === null || option === null) return undefined;
    let cancelled = false;
    const observer = new ResizeObserver(() => chart.current?.resize());
    /*
     * The host div is in the DOM synchronously (the tests hit-test it); only the drawing waits
     * for the chunk. `cancelled` covers an unmount that lands before the import resolves.
     */
    void loadChartRuntime().then(({ initChart }) => {
      if (cancelled) return;
      chart.current ??= initChart(element);
      chart.current.setOption(option, true);
      observer.observe(element);
    });
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [option]);

  useEffect(
    () => () => {
      chart.current?.dispose();
      chart.current = null;
    },
    [],
  );

  if (data === undefined) {
    return <div className="ez-widget-empty" data-testid="widget-loading">Loading…</div>;
  }
  if (data.buckets.length === 0) {
    return (
      <div className="ez-widget-empty" data-testid="widget-no-data">
        Nothing to chart yet — add items, or pick a column with values.
      </div>
    );
  }
  return <div className="ez-widget-chart" data-testid="widget-chart" ref={host} />;
}

export { SERIES_PALETTE };
